import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";

import { blurActiveElement } from "@utils/blur";
import { useBreakpoint } from "@utils/breakpoints/breakpoints.provider";
import { isDefined } from "@utils/helpers";

import { useThunkDispatch } from "@redux/dispatcher";
import * as CartModule from "@redux/modules/api/data/cart/cart.module";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import {
    MobileProductFacets,
    ProductFacets,
} from "@components/Search/SearchResults/StateComponents/Facets/facetsComponents";
import { ProductHitsPerPage } from "@components/Search/SearchResults/StateComponents/HitsPerPage/hitsPerPageComponents";
import { SEARCH_PRODUCT_ID } from "@components/Search/SearchResults/StateComponents/NumberOfHits/numberOfHits.container";
import { ProductPagination } from "@components/Search/SearchResults/StateComponents/Pagination/paginationComponents";
import { ProductResultList } from "@components/Search/SearchResults/StateComponents/ResultList/resultListComponents";
import { SelectedFacets } from "@components/Search/SearchResults/StateComponents/SelectedFacets/selectedFacets.container";
import { ProductSort } from "@components/Search/SearchResults/StateComponents/Sort/sortComponents";

import { OpenFiltersButton } from "./components/OpenFiltersButton/openFiltersButton.component";
import { SearchResultProductTileConfig } from "./searchResults.container";

type ProductSearchResultsComponentProps = {
    productTileConfig?: SearchResultProductTileConfig;
    showFacets: boolean;
};

const ProductSearchResultsComponent = ({
    productTileConfig,
    showFacets,
}: ProductSearchResultsComponentProps) => {
    const { t } = useGranite();
    const dispatch = useThunkDispatch();

    const { breakpoints } = useBreakpoint();
    const isMobile = !breakpoints.lg;
    const [mobileFacetsOpen, setMobileFacetsOpen] = useState(false);

    const filtersRef = useRef<HTMLDivElement>(null);
    const stickyFiltersRef = useRef<HTMLDivElement>(null);
    const stickFilters = useRef<boolean>(false);

    const handleOnCloseMobileProductFacets = () =>
        setMobileFacetsOpen(() => false);

    useEffect(() => {
        dispatch(CartModule.load());
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (!filtersRef.current) return;
            if (!stickyFiltersRef.current) return;

            const filtersY = filtersRef.current?.getBoundingClientRect().y;

            const stickFiltersToTop = isDefined(filtersY) && filtersY <= 0;

            if (stickFilters.current !== stickFiltersToTop) {
                if (stickFiltersToTop) {
                    blurActiveElement();
                    stickyFiltersRef.current.classList.remove("d-none");
                    stickyFiltersRef.current.classList.add("d-flex");
                } else {
                    stickyFiltersRef.current.classList.remove("d-flex");
                    stickyFiltersRef.current.classList.add("d-none");
                }

                stickFilters.current = stickFiltersToTop;
            }
        };

        document.addEventListener("scroll", handleScroll);

        return () => {
            document.removeEventListener("scroll", handleScroll);
        };
    }, [filtersRef, stickFilters]);

    return (
        <>
            {isMobile && showFacets && (
                <>
                    <div
                        className={
                            "search__product__filters--sticky py-2 border-bottom position-fixed w-100 align-items-center d-none"
                        }
                        ref={stickyFiltersRef}
                    >
                        <div className={"w-100"}>
                            <OpenFiltersButton
                                className={"d-lg-none"}
                                setOpen={setMobileFacetsOpen}
                            />
                        </div>
                        <div className={"w-100"}>
                            <ProductSort />
                        </div>
                    </div>
                    <MobileProductFacets
                        isOpen={mobileFacetsOpen}
                        onClose={handleOnCloseMobileProductFacets}
                        className={"d-lg-none"}
                    />
                </>
            )}
            {!isMobile && showFacets && (
                <section className={"facets--product d-none d-lg-block"}>
                    <ProductFacets />
                </section>
            )}

            <article
                id={SEARCH_PRODUCT_ID}
                className={classNames("search__product", {
                    "search__product--no-facets": !showFacets,
                })}
            >
                {showFacets && (
                    <header
                        className={
                            "d-sm-flex justify-content-between align-items-center"
                        }
                    >
                        <h3 className={"m-0"}>
                            {t("search.results.title.products")}
                        </h3>
                        <div
                            ref={filtersRef}
                            className={
                                "search__product__filters justify-content-between justify-content-md-end align-items-center py-2"
                            }
                        >
                            {isMobile && (
                                <OpenFiltersButton
                                    className={"d-lg-none"}
                                    setOpen={setMobileFacetsOpen}
                                />
                            )}
                            <ProductHitsPerPage
                                className={"d-none d-md-flex"}
                            />
                            <ProductSort
                                className={classNames({
                                    "search__product__filters__product-sort":
                                        isMobile,
                                })}
                            />
                        </div>
                    </header>
                )}

                <SelectedFacets component={"PRODUCT_FACETS"} />
                <ProductResultList productTileConfig={productTileConfig} />
                <ProductPagination />
            </article>
        </>
    );
};

export { ProductSearchResultsComponent };
